import React, { Suspense, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { contentFocusIndicatorFocusedTargetClass } from 'shared/constants.platform';
import { openHighlightGptSubMenu } from 'shared/foreground/cmdPalette';
import foregroundEventEmitter from 'shared/foreground/eventEmitter';
import { globalState, updateState } from 'shared/foreground/models';
import contentFrame, {
  portalGate as portalGateToContentFrame,
} from 'shared/foreground/portalGates/contentFrame/to/reactNativeWebview';
import background from 'shared/foreground/portalGates/toBackground';
import { getDocument } from 'shared/foreground/stateGetters';
import { useTTSWordBoundariesForVoice } from 'shared/foreground/stateHooks';
import { useIsTtsPlayerStateForThisDocument } from 'shared/foreground/stateHooks/tts';
import { useIsAutoHighlightingEnabled } from 'shared/foreground/stateHooks/useIsAutoHighlightingEnabled';
import useWordBoundaryOffset from 'shared/foreground/stateHooks/useWordBoundaryOffset';
import { deleteHighlight } from 'shared/foreground/stateUpdaters/persistentStateUpdaters/documents/highlight';
import { reparseUserYoutubeDocumentIfNeeded, setVideoEnhancedTranscriptStatus } from 'shared/foreground/stateUpdaters/persistentStateUpdaters/documents/video';
import {
  toggleIsAutoHighlightingEnabled,
} from 'shared/foreground/stateUpdaters/persistentStateUpdaters/highlighterSettings';
import { setFocusedHighlightId } from 'shared/foreground/stateUpdaters/transientStateUpdaters/other';
import type { HighlightElement } from 'shared/foreground/types';
import copyTextToClipboard from 'shared/foreground/utils/copyTextToClipboard';
import { useFetchWordBoundariesThrottled } from 'shared/foreground/utils/fetchWordBoundaries';
import forwardRef from 'shared/foreground/utils/forwardRef';
import { splitSerializedRange } from 'shared/foreground/utils/locationSerialization/utils';
import { useReadingProgressTracking } from 'shared/foreground/utils/useReadingProgressTracking';
import useShouldProgressivelyRenderHighlights from 'shared/foreground/utils/useShouldProgressivelyRenderHighlights';
import WebContentFramePuppeteer from 'shared/foreground/WebContentFramePuppeteer';
import {
  AnchorScrollTarget,
  Article,
  BaseDocument,
  Category,
  FirstClassDocument,
  Highlight,
  LenientReadingPosition,
  TextDirection,
  YouTubeEnhancedTranscriptStatus,
} from 'shared/types';
import { ChunkSanitizationOptions, UNCHUNKED_DOCUMENT_CHUNK_ID, WebviewChunks } from 'shared/types/chunkedDocuments';
import { ShortcutId } from 'shared/types/keyboardShortcuts';
import { TrackPlayerState } from 'shared/types/tts';
import { isYouTubeUrl } from 'shared/typeValidators';
import { isDesktopApp, os } from 'shared/utils/environment';
import getDocumentDomain from 'shared/utils/getDocumentDomain';
import promiseAny from 'shared/utils/promiseAny.platform';
import { rwSanitizeHtml } from 'shared/utils/rwSanitizeHtml';
import useDebounce from 'shared/utils/useDebounce';

import { useAppearanceStyles } from '../hooks/appearanceStyles';
import { useDocumentTextContentClassName } from '../hooks/useDocumentTextContentClassName';
import { useKeyboardShortcut } from '../hooks/useKeyboardShortcut';
import useScrollLocation, { getScrollLocation, scrollToPosition } from '../hooks/useScrollLocation';
import { reactLazy } from '../utils/dynamicImport';
import { gptPromptInput } from '../utils/gpt';
import { useShortcutsMap } from '../utils/shortcuts';
import useLocation from '../utils/useLocation';
import ContentFocusIndicator from './ContentFocusIndicator';
import { DocumentFrontMatter } from './DocumentFrontMatter';
import styles from './DocumentTextContent.module.css';
import HighlighterPopovers from './HighlighterPopovers';
import { openSingleParentNotebookView } from './NotebookView/notebookHelpers';
import SaveLinkInAppPopover from './Popovers/SaveLinkInAppPopover';
import ReadingProgressBar from './ReadingProgressBar';
import { ReturnToReadingButton } from './ReturnToReadingButton';

declare let window: WindowWithAPIsMissingFromTypeScript;

function useTtsWordTracking({
  documentId,
  contentContainerId,
  scrollableRootId,
}: {
  contentContainerId: string;
  scrollableRootId: string;
  documentId: FirstClassDocument['id'];
}) {
  const tts = globalState((state) => state.tts);
  const isTtsActiveForThisDocument = useMemo(
    () => Boolean(documentId && documentId === tts?.playingDocId),
    [documentId, tts?.playingDocId],
  );

  useEffect(() => {
    // Unset some variables to be safe (mobile doesn't need to do this because the webview is destroyed)
    contentFrame.onDocumentIdChangedForTts();
  }, [documentId]);

  const ttsWordBoundariesForVoice = useTTSWordBoundariesForVoice(documentId);
  useEffect(() => {
    contentFrame.setTtsWordBoundariesForVoice(ttsWordBoundariesForVoice);
  }, [ttsWordBoundariesForVoice]);

  const wordBoundaryOffset = useWordBoundaryOffset(documentId);

  const ttsPlayerStateForThisDocument = useIsTtsPlayerStateForThisDocument(documentId);
  const isTrackPlayerPlaying = useMemo(
    () => ttsPlayerStateForThisDocument === TrackPlayerState.Playing,
    [ttsPlayerStateForThisDocument],
  );
  const debouncedTrackPlayerPosition = useDebounce(tts?.trackPlayerInfo?.position, 100);

  const isAutoScrollEnabled = useMemo(() => Boolean(tts?.autoScrolling), [tts]);

  useEffect(() => {
    if (!isTtsActiveForThisDocument) {
      contentFrame.hideWordBoundaryIndicator();
      return;
    }

    const position = debouncedTrackPlayerPosition + 0.5 + wordBoundaryOffset / 1000;
    contentFrame.updateWordBoundaryIndicator({
      contentContainerId,
      // TODO: FIX CHUNKED DOCS (use proper Ids here when ready)
      chunkId: UNCHUNKED_DOCUMENT_CHUNK_ID,
      isAutoScrollEnabled,
      scrollableRootId,
      position,
    });
  }, [
    contentContainerId,
    debouncedTrackPlayerPosition,
    isAutoScrollEnabled,
    isTrackPlayerPlaying,
    isTtsActiveForThisDocument,
    scrollableRootId,
    wordBoundaryOffset,
  ]);

  const fetchWordBoundariesThrottled = useFetchWordBoundariesThrottled();

  useEffect(() => {
    portalGateToContentFrame.on('fetch-word-boundaries', fetchWordBoundariesThrottled);
    return () => {
      portalGateToContentFrame.off('fetch-word-boundaries', fetchWordBoundariesThrottled);
    };
  }, [fetchWordBoundariesThrottled]);
}

const EmbeddedYoutubeDocument = reactLazy(() => import('./DocumentReader/EmbeddedYoutubeDocument'));

async function getFirstHighlightId(): Promise<string | undefined> {
  // Grab the first highlight ID
  return (
    await foregroundEventEmitter.emitAsync('getHighlightIdsInSelector', {
      selector: `.${contentFocusIndicatorFocusedTargetClass}`,
    })
  )
    .find(Boolean) // first truthy result from event handlers
    // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
    .find(Boolean); // first highlight ID
}

const getOrCreateFirstHighlightId = async ({
  userInteraction = 'unknown',
  temporary = false,
}: {
  userInteraction?: string;
  temporary?: boolean;
}): Promise<{ didCreate: boolean; highlightId: Highlight['id'] | undefined; }> => {
  const hasSelection = window.getSelection()?.toString();
  const selector = hasSelection ? undefined : `.${contentFocusIndicatorFocusedTargetClass}`;

  let highlightId = await getFirstHighlightId();
  let didCreate = false;

  if (!highlightId || hasSelection) {
    highlightId = (
      await foregroundEventEmitter.emitAsync('highlight', {
        selector,
        userInteraction,
        temporary,
      })
    ).find(Boolean) as string | undefined;
    didCreate = Boolean(highlightId);
  }

  return { didCreate, highlightId };
};

const getContentFromFirstHighlight = async () => {
  const highlightId = await getFirstHighlightId();
  if (!highlightId) {
    return;
  }
  return (await contentFrame.getKnownHighlights())?.[highlightId]?.content?.trim();
};

export type Props = {
  author?: string;
  category: Category;
  chunks: WebviewChunks;
  setChunkContentState: (chunkId: string, state: 'loaded' | 'unloaded') => void;
  currentScrollPosition?: FirstClassDocument['currentScrollPosition'];
  docId: FirstClassDocument['id'];
  faviconUrl?: BaseDocument['favicon_url'];
  highlights: Highlight[];
  languageCode?: BaseDocument['language'];
  onNewFocusTarget?: (newTarget: HTMLElement | void) => void;
  originUrl?: string;
  publishedDate: FirstClassDocument['published_date'] | undefined;
  readingPercent: number;
  readingPosition?: FirstClassDocument['readingPosition'];
  rssSourceName?: string;
  scrollableAncestorRef: React.MutableRefObject<HTMLElement>;
  scrollPercent: number;
  siteName?: BaseDocument['site_name'];
  sourceSpecificData: FirstClassDocument['source_specific_data'];
  sourceUrl?: string;
  tags: BaseDocument['tags'];
  title: string;
  wordCount: number;
};

const defaultExport = React.memo(
  forwardRef<Props, HTMLDivElement>(function DocumentTextContent(
    {
      author,
      category,
      chunks,
      setChunkContentState,
      currentScrollPosition,
      docId,
      faviconUrl,
      highlights,
      languageCode,
      onNewFocusTarget = () => null,
      originUrl,
      publishedDate,
      readingPosition,
      rssSourceName,
      scrollableAncestorRef,
      siteName,
      sourceSpecificData,
      sourceUrl,
      tags,
      title,
      wordCount,
    },
    elementRef,
  ): JSX.Element {
    const elementId = 'document-text-content';

    const highlightIdToOpenAt = globalState(useCallback((state) => state.highlightIdToOpenAt, []));
    const textDirection = globalState(
      useCallback((state) => state.client.readerSettings.desktop.direction, []),
    );
    const isVideoHeaderShown = globalState(useCallback((state) => state.isVideoHeaderShown, []));
    const highlightToOpenAt = highlightIdToOpenAt
      ? highlights.find(({ id }) => id === highlightIdToOpenAt)
      : null;
    const [highlightElements, setHighlightElements] = useState<HighlightElement[]>([]);
    const [isContentFrameManagerActive, setIsContentFrameManagerActive] = useState(false);
    const isAutoHighlightingEnabled = useIsAutoHighlightingEnabled();
    const [canRenderPopovers, setCanRenderPopovers] = useState(false);
    const [
      failedExtensionOrYouTubeHighlightsHighlightIds,
      setFailedExtensionOrYouTubeHighlightsHighlightIds,
    ] = useState<Highlight['id'][]>([]);
    const shortcutsMap = useShortcutsMap();
    const history = useHistory();

    const iframeWrapperRef = useRef<HTMLDivElement>(null);
    const isYouTubeVideo = useMemo(() => isYouTubeUrl(sourceUrl || ''), [sourceUrl]);
    const [isResizingYouTubeVideo, setIsResizingYouTubeVideo] = useState(false);
    const [currentScrollOffsetY, setCurrentScrollOffsetY] = useState(0);

    const isOriginalEmailView = Boolean(sourceSpecificData?.email?.originalEmailView);
    const enhancedYouTubeTranscriptStatus = sourceSpecificData?.video?.enhanced_youtube_transcript_status;
    const showEnhancedYouTubeTranscript = Boolean(
      enhancedYouTubeTranscriptStatus === YouTubeEnhancedTranscriptStatus.NeedsToDownload ? false : sourceSpecificData?.video?.show_enhanced_youtube_transcript,
    );

    const sanitizationOptions: ChunkSanitizationOptions = useMemo(() => ({
      showEnhancedYouTubeTranscript,
      isOriginalEmailView,
      category,
    }), [category, isOriginalEmailView, showEnhancedYouTubeTranscript]);

    const youtubeChunkContent = useMemo(() => chunks.document[0]?.content || '', [chunks]);

    // YouTube videos are always a one-chunk document, so we just grab the transcript HTML from the first chunk.
    const transcriptHtml = useMemo(() =>
      rwSanitizeHtml(
        isYouTubeVideo ? youtubeChunkContent : '',
        category,
        isOriginalEmailView,
        showEnhancedYouTubeTranscript,
      ), [category, isOriginalEmailView, isYouTubeVideo, showEnhancedYouTubeTranscript, youtubeChunkContent]);

    // This is a hack to ensure that the new enhanced YouTube transcript gets downloaded when the enhancement is done in the server
    useEffect(() => {
      if (sourceSpecificData?.video?.enhanced_youtube_transcript_status !== YouTubeEnhancedTranscriptStatus.NeedsToDownload) {
        return;
      }

      if (youtubeChunkContent.includes('rw-enhanced-youtube-transcript')) {
        // It already has the enhanced transcript, so we set the status to done
        setVideoEnhancedTranscriptStatus(docId, YouTubeEnhancedTranscriptStatus.Done, { userInteraction: 'unknown' });
      } else {
        // It doesn't have the enhanced transcript yet, so we poll the latest state
        background.pollLatestState(40);
      }
    }, [chunks, docId, sourceSpecificData?.video?.enhanced_youtube_transcript_status, youtubeChunkContent]);

    useTtsWordTracking({
      contentContainerId: elementId,
      documentId: docId,
      scrollableRootId: scrollableAncestorRef.current.id,
    });

    useKeyboardShortcut(
      shortcutsMap[ShortcutId.ToggleNotebookView],
      useCallback(() => {
        if (highlights.length === 0) {
          return;
        }
        openSingleParentNotebookView(history, docId);
      }, [highlights, docId, history]),
    );

    const [element, setElement] = useState<HTMLElement>(elementRef.current);
    useEffect(() => {
      if (!element) {
        return;
      }

      const timeouts = [
        setTimeout(() => setIsContentFrameManagerActive(true), 50),
        setTimeout(() => setCanRenderPopovers(true), 500),
      ];

      return () => {
        for (const id of timeouts) {
          clearTimeout(id);
        }
      };
    }, [element, elementRef]);

    const docIdRef: React.MutableRefObject<Article['id']> = useRef(docId);

    const [shouldHighlightIconsBeHidden, setShouldHighlightIconsBeHidden] = useState(false);

    const setElementRef = useCallback(
      (value: HTMLDivElement) => {
        elementRef.current = value;
        setElement(elementRef.current);
      },
      [elementRef],
    );

    useAppearanceStyles();

    const zenMode = globalState(useCallback((state) => state.zenModeEnabled, []));
    const [latestScrollPosition, setLatestScrollPosition] = useState<LenientReadingPosition>(
      currentScrollPosition ?? { scrollDepth: 0, serializedPosition: null },
    );
    const [isReadingEnabled, setIsReadingEnabled] = useState(false);
    useEffect(() => {
      docIdRef.current = docId;
    }, [docId]);

    useEffect(() => {
      if (!elementRef.current) {
        return;
      }
      // Child nodes do not have class list as property
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      if (elementRef.current.childNodes.length && elementRef.current.childNodes[0].classList) {
        // Apply the zen mode style on the child node
        if (zenMode) {
          // Child nodes do not have class list as property
          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
          // @ts-ignore
          // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
          elementRef.current.childNodes[0].classList.add(styles.zenMode);
        } else {
          // Child nodes do not have class list as property
          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
          // @ts-ignore
          // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
          elementRef.current.childNodes[0].classList.remove(styles.zenMode);
        }
      }
    }, [elementRef, zenMode, chunks.document.length]);

    useEffect(() => {
      // when you click a normal link in a doc, you're supposed to get the link popover
      // but when you click a "View content" link, it should open in a browser tab
      // on desktop this means you have add a special data-open-on-desktop attribute but ONLY to "View content" links

      if (!isDesktopApp) {
        return;
      }
      if (!elementRef.current) {
        return;
      }
      elementRef.current.querySelectorAll('p.rw-outer-content > a').forEach((a) => {
        (a as HTMLAnchorElement).dataset.openOnDesktop = 'true';
      });
    }, [elementRef]);

    const { hash, search } = useLocation();
    const anchorScrollTarget: AnchorScrollTarget | undefined = useMemo(() => {
      if (!isContentFrameManagerActive) {
        return undefined;
      }
      const query = new URLSearchParams(search);
      if (query.get('scroll') !== 'anchor') {
        return undefined;
      }
      const url = query.get('url');
      if (!url) {
        throw new Error('no url search param');
      }
      const anchorText = query.get('anchorText') || '';
      return { url, anchorText };
    }, [search, isContentFrameManagerActive]);

    /*
    Reading position
  */
    const initialScrollLocation = useMemo(() => {
      // Skip if they went directly to this document with a fragment identifier
      if (hash?.length > 1) {
        return;
      }
      return currentScrollPosition;
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [docId]);

    // Reset scroll position after docId changes
    useEffect(() => {
      scrollToPosition(scrollableAncestorRef, elementRef, { scrollDepth: 0, serializedPosition: null });
    }, [docId, scrollableAncestorRef, elementRef]);

    const customGetReadingPosition = useCallback(async () => {
      const newReadingPosition = await getScrollLocation({
        contentRootRef: elementRef.current,
        scrollableRootRef: scrollableAncestorRef.current,
        documentChunks: chunks.document,
      });
      return newReadingPosition;
    }, [elementRef, scrollableAncestorRef, chunks.document]);

    const updateSkimmingTimerCallback = useCallback((val: number) => {
      // Empty for now, but will be updated when designed.
    }, []);


    const initialWordsRead = useMemo(() =>
      (currentScrollPosition?.scrollDepth ?? 0) * wordCount,
      [wordCount, currentScrollPosition?.scrollDepth]);

    const { updateReadingProgress: updateCurrentScrollPos, isUserReading: isReading } =
      useReadingProgressTracking({
        docId: docIdRef.current,
        initialReadingPosition: readingPosition ?? null,
        transformReadingPosition: customGetReadingPosition,
        wpmThreshold: 700,
        isEnabled: isReadingEnabled,
        updateSkimmingTimer: updateSkimmingTimerCallback,
        totalWordsInDocument: wordCount,
        initialWordsRead,
      });

    useEffect(() => {
      if (!readingPosition) {
        setIsReadingEnabled(true);
      } else {
        setTimeout(() => {
          setIsReadingEnabled(true);
        }, 15000);
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const initialLocation = useMemo(
      // Don't go to last position if we're scrolling to a highlight
      () => highlightIdToOpenAt ? undefined : initialScrollLocation ?? undefined,
      [highlightIdToOpenAt, initialScrollLocation],
    );
    useEffect(() => {
      if (initialLocation?.serializedPosition) {
        // initial scroll location loaded with existing serialized position. Let onFirstScroll() start observers.
        return;
      }
      // if we don't have an initial scroll location, ensure we still load/unload chunk content as we scroll.
      portalGateToContentFrame.methods.startChunkContainerIntersectionObservers();
    }, [initialLocation]);
    useScrollLocation({
      contentRootRef: elementRef as React.MutableRefObject<HTMLElement>,
      initialLocation,
      scrollableRootRef: scrollableAncestorRef,
      documentChunks: chunks.document,
      onFirstScroll: () => {
        if (
          readingPosition?.scrollDepth === currentScrollPosition?.scrollDepth ||
          readingPosition?.serializedPosition === currentScrollPosition?.serializedPosition
        ) {
          setIsReadingEnabled(true);
          // hideSidebars(true, {userInteraction: 'unknown'});
        }
        // to ensure we don't create chunk loading jankiness while performing the initial scroll,
        // we start the intersection observers after initial scroll completes.
        portalGateToContentFrame.methods.startChunkContainerIntersectionObservers();
      },
      onScroll: (newScrollPosition, currentScrollOffsetY) => {
        // Resizing the YouTube video triggers a scroll event, and we don't want to update the scroll position in this case
        if (!updateCurrentScrollPos || isResizingYouTubeVideo) {
          return;
        }

        updateCurrentScrollPos(newScrollPosition);
        setLatestScrollPosition(newScrollPosition);
        setCurrentScrollOffsetY(currentScrollOffsetY);
      },
    });

    const returnToReadingPosition = useCallback(() => {
      scrollToPosition(
        scrollableAncestorRef,
        elementRef,
        readingPosition ?? { scrollDepth: 0, serializedPosition: null },
      );
      setTimeout(() => {
        setIsReadingEnabled(true);
      }, 3000);
    }, [scrollableAncestorRef, elementRef, readingPosition]);

    useKeyboardShortcut(
      shortcutsMap[ShortcutId.PageDown],
      useCallback(() => {
        if (scrollableAncestorRef.current !== null) {
          scrollableAncestorRef.current.focus();
        }
      }, [scrollableAncestorRef]),
    );

    useKeyboardShortcut(
      shortcutsMap[ShortcutId.PageUp],
      useCallback(() => {
        if (scrollableAncestorRef.current !== null) {
          scrollableAncestorRef.current.focus();
        }
      }, [scrollableAncestorRef]),
    );

    useKeyboardShortcut(
      shortcutsMap[ShortcutId.Up],
      useCallback(() => {
        if (scrollableAncestorRef.current !== null) {
          scrollableAncestorRef.current.focus();
        }
      }, [scrollableAncestorRef]),
      {
        description: 'Scroll up',
      },
    );

    useKeyboardShortcut(
      shortcutsMap[ShortcutId.Down],
      useCallback(() => {
        if (scrollableAncestorRef.current !== null) {
          scrollableAncestorRef.current.focus();
        }
      }, [scrollableAncestorRef]),
      {
        description: 'Scroll down',
      },
    );

    // If we're opening at a highlight, focus it / an ancestor of it
    const initialContentFocusIndicatorPosition =
      highlightToOpenAt?.location && splitSerializedRange(highlightToOpenAt.location)?.start ||
      initialScrollLocation?.serializedPosition;

    const focusTargetRef = useRef<HTMLElement>();

    useKeyboardShortcut(
      shortcutsMap[ShortcutId.Highlight],
      useCallback(async () => {
        if (window.getSelection()?.toString()) {
          foregroundEventEmitter.emit('highlight', {
            collisonOutcome: 'merge',
            userInteraction: 'keyup',
          });
          return;
        }

        if (!focusTargetRef.current) {
          return;
        }

        // If the existing highlight covers the entire focus indicator, then remove
        // it on the hotkey!
        const highlightContent = await getContentFromFirstHighlight();
        const focusContent = focusTargetRef.current.textContent;

        // Remove Word-Joiner character (used in resize handles)
        const cleanFocusContent = focusContent?.replace(/\u2060/g, '').trim();
        if (highlightContent?.trim() === cleanFocusContent) {
          deleteHighlight((await getFirstHighlightId()) as string, { userInteraction: 'keyup' });
        } else {
          // otherwise, create a new highlight and merge any existing ones
          foregroundEventEmitter.emit('highlight', {
            collisonOutcome: 'merge',
            selector: `.${contentFocusIndicatorFocusedTargetClass}`,
            userInteraction: 'keyup',
          });
        }
      }, [focusTargetRef]),
      {
        description: 'Highlight current section',
      },
    );

    useEffect(() => {
      // Event listener sits here to access the elementRef (the article body) and
      // the focus indicator.
      const gpt = async (highlightId: string) => {
        const highlight = await getDocument<Highlight>(highlightId);
        if (!highlight) {
          throw new Error('no highlight');
        }
        // e.g. highlighting a header grabs content from child headers
        const expandedSelection = await gptPromptInput(highlight, focusTargetRef, elementRef);
        const focusIndicatorContents = focusTargetRef.current?.textContent;

        await setFocusedHighlightId(highlightId);
        await updateState(
          (state) => {
            if (!expandedSelection) {
              return;
            }
            state.gptPrompt = {
              prompt: '',
              selection: highlight.markdown,
              expandedSelection,
              surroundingParagraphContents: focusIndicatorContents || '',
            };
          },
          { eventName: 'cmd-palette-gpt-opening', userInteraction: 'keyup' },
        );

        openHighlightGptSubMenu();
      };
      foregroundEventEmitter.on('gpt:open', gpt);

      return () => {
        foregroundEventEmitter.off('gpt:open', gpt);
      };
    }, [focusTargetRef, elementRef]);

    useKeyboardShortcut(
      shortcutsMap[ShortcutId.Ghostreader],
      useCallback(async (event) => {
        event.preventDefault();
        const { highlightId } = await getOrCreateFirstHighlightId({ userInteraction: 'keyup' });
        if (!highlightId) {
          return;
        }
        foregroundEventEmitter.emit(`annotationPopover-${highlightId}:hide`); // Hide when auto-highlighting
        foregroundEventEmitter.emit('gpt:open', highlightId);
      }, []),
      {
        description: 'Invoke Ghostreader',
      },
    );

    useKeyboardShortcut(
      shortcutsMap[ShortcutId.ViewHighlightInNotebookView],
      useCallback(async () => {
        const highlightId = await getFirstHighlightId();
        if (!highlightId) {
          return;
        }
        foregroundEventEmitter.emit(`annotationPopover-${highlightId}:hide`); // Hide when auto-highlighting
        openSingleParentNotebookView(history, docId, highlightId);
      }, [docId, history]),
      {
        description: 'View in Notebook',
      },
    );

    // Open note field of (existing / new) highlight
    useKeyboardShortcut(
      shortcutsMap[ShortcutId.Note],
      useCallback(async () => {
        if (!focusTargetRef.current) {
          return;
        }
        const { didCreate, highlightId } = await getOrCreateFirstHighlightId({
          userInteraction: 'keyup',
        });
        if (!highlightId) {
          return;
        }

        await promiseAny([
          foregroundEventEmitter
            .emitAsync(`annotationPopover-${highlightId}:is-listening?`)
            .then((results) => {
              if (!results.some(Boolean)) {
                throw new Error('None truthy');
              }
            }),
          foregroundEventEmitter.waitFor(`annotationPopover-${highlightId}:listening`),
        ]);
        foregroundEventEmitter.emit(`annotationPopover-${highlightId}:openHighlightNoteForm`, {
          shouldRemoveHighlightOnCancel: didCreate,
        });
      }, [focusTargetRef]),
      {
        description: 'Add / Edit note on current highlight',
      },
    );

    /*
    It seems hot-keys can't assign to cmd/ctrl+c so we need to listen to all keydown events.
    How this should work: https://linear.app/readwise/issue/RW-6149/support-copying-text-of-highlights-in-reader
  */
    useKeyboardShortcut(
      shortcutsMap[ShortcutId.Wildcard],
      useCallback(
        async (event) => {
          if (document.getElementById('notebook-sidebar-panel')?.contains(document.activeElement)) {
            return;
          }

          const cmdOrCtrlPropertyName = os.name.includes('Mac') ? 'metaKey' : 'ctrlKey';
          if (
            !(
              event.nativeKeyboardEvent &&
              event.nativeKeyboardEvent[cmdOrCtrlPropertyName] &&
              event.nativeKeyboardEvent.key.toLowerCase() === 'c'
            ) ||
            window.getSelection()?.toString() ||
            !focusTargetRef.current
          ) {
            return;
          }

          const text = await getContentFromFirstHighlight() ?? focusTargetRef.current.innerText;
          if (!text) {
            return;
          }
          copyTextToClipboard(text);
        },
        [focusTargetRef],
      ),
      {
        description: 'Copy text to clipboard',
        preferredEventTrigger: 'keydown',
      },
    );

    useKeyboardShortcut(
      shortcutsMap[ShortcutId.ToggleAutoHighlighting],
      useCallback(() => toggleIsAutoHighlightingEnabled('keyup'), []),
      {
        description: 'Toggle auto-highlighting',
        preferredEventTrigger: 'keyup',
      },
    );

    useKeyboardShortcut(
      shortcutsMap[ShortcutId.Tags],
      useCallback(async () => {
        const { didCreate, highlightId } = await getOrCreateFirstHighlightId({
          userInteraction: 'keyup',
        });
        if (!highlightId) {
          return;
        }

        await promiseAny([
          foregroundEventEmitter
            .emitAsync(`annotationPopover-${highlightId}:is-listening?`)
            .then((results: (boolean | void)[]) => {
              if (!results.some(Boolean)) {
                throw new Error('None truthy');
              }
            }),
          foregroundEventEmitter.waitFor(`annotationPopover-${highlightId}:listening`),
        ]);
        foregroundEventEmitter.emit(`annotationPopover-${highlightId}:openHighlightTagsForm`, {
          shouldRemoveHighlightOnCancel: didCreate,
        });
      }, []),
      {
        description: 'Add / Edit tags on current highlight',
        preferredEventTrigger: 'keyup',
      },
    );

    const [isAltPressed, setIsAltPressed] = useState(false);
    const [isMouseDown, setIsMouseDown] = useState(false);
    const [wasAltPressedWhenLastMouseInteractionEnded, setWasAltPressedWhenLastMouseInteractionEnded] =
      useState(false);
    useEffect(() => {
      const listeners: { [name: string]: (event: KeyboardEvent) => void; } = {};
      const on = (name: string, callback: (event: KeyboardEvent) => void) => {
        listeners[name] = callback;
        document.addEventListener(name, callback as EventListener);
      };

      on('keydown', (event) => setIsAltPressed(Boolean(event.altKey)));
      on('keyup', (event) => setIsAltPressed(Boolean(event.altKey)));
      on('mousedown', () => {
        setIsMouseDown(true);
        setWasAltPressedWhenLastMouseInteractionEnded(false);
      });
      on('mouseup', (event) => {
        setIsMouseDown(false);
        setWasAltPressedWhenLastMouseInteractionEnded(event.altKey);
      });

      return () => {
        for (const [name, callback] of Object.entries(listeners)) {
          document.removeEventListener(name, callback as EventListener);
        }
      };
    }, []);

    const readingPercent = readingPosition?.scrollDepth ? readingPosition.scrollDepth * 100 : 0;
    const scrollPercent = latestScrollPosition?.scrollDepth ? latestScrollPosition.scrollDepth * 100 : 0;

    const onFocusTargetChange = useCallback(
      (newTarget: Element | void) => {
        foregroundEventEmitter.emit('content-focus-indicator:new-focus-target', newTarget);
        onNewFocusTarget(newTarget as HTMLElement | undefined);
      },
      [onNewFocusTarget],
    );

    const contentClasses: string[] = useMemo(() => {
      const results = [styles.content];

      if (
        isMouseDown
          ? isAutoHighlightingEnabled
            ? !isAltPressed
            : isAltPressed
          : isAutoHighlightingEnabled
            ? !wasAltPressedWhenLastMouseInteractionEnded
            : wasAltPressedWhenLastMouseInteractionEnded
      ) {
        results.push('document-text-content--auto-highlighting-enabled');
      }
      if (zenMode) {
        results.push(styles.zenMode);
      }
      if (shouldHighlightIconsBeHidden) {
        results.push(styles.contentWithoutHighlightIcons);
      }

      if (isYouTubeVideo) {
        results.push('is-youtube-video');
      }

      if (textDirection === TextDirection.RightToLeft) {
        results.push(styles.rtl);
      }

      if (sourceSpecificData?.epub?.originalStylesEnabled) {
        results.push('epub-original-styles');
      }

      results.push('rw-chunk-containers-root');

      return results;
    }, [
      isMouseDown,
      isAutoHighlightingEnabled,
      isAltPressed,
      wasAltPressedWhenLastMouseInteractionEnded,
      zenMode,
      shouldHighlightIconsBeHidden,
      textDirection,
      sourceSpecificData?.epub?.originalStylesEnabled,
      isYouTubeVideo,
    ]);

    const documentTextContentClassName = useDocumentTextContentClassName(contentClasses, isOriginalEmailView);

    const shouldProgressivelyRenderHighlights = useShouldProgressivelyRenderHighlights(docId);

    const nameOrDomain = getDocumentDomain({ rssSourceName, siteName, originUrl });

    useEffect(() => {
      if (!isYouTubeVideo) {
        return;
      }

      reparseUserYoutubeDocumentIfNeeded(docId, sourceSpecificData, {
        userInteraction: 'unknown',
      });
    }, [docId, isYouTubeVideo, sourceSpecificData]);

    return (
      <div className={`${styles.root}`} id="root">
        <div className={styles.progressBarContainer}>
          <ReadingProgressBar
            progress={readingPercent}
            currentScroll={scrollPercent}
            fakeReadingProgress={isReading}
            large
          />
        </div>
        <Suspense fallback={null}>
          {sourceUrl && isYouTubeVideo
            ? <EmbeddedYoutubeDocument
                ref={iframeWrapperRef}
                docId={docId}
                url={sourceUrl}
                transcriptHtml={transcriptHtml}
                scrollDepth={readingPosition?.scrollDepth}
                onVideoResizingStateChange={setIsResizingYouTubeVideo}
                contentCurrentScrollOffsetY={currentScrollOffsetY}
                showEnhancedYouTubeTranscript={showEnhancedYouTubeTranscript}
                failedExtensionOrYouTubeHighlightsHighlightIds={
                  failedExtensionOrYouTubeHighlightsHighlightIds
                }
              />
           : <DocumentFrontMatter
               author={author}
               category={category}
               docId={docId}
               failedExtensionOrYouTubeHighlightsHighlightIds={
                failedExtensionOrYouTubeHighlightsHighlightIds
              }
               faviconUrl={faviconUrl}
               languageCode={languageCode}
               nameOrDomain={nameOrDomain}
               publishedOrLastHighlightDate={publishedDate}
               tags={tags}
               title={title}
               wordCount={wordCount}
               url={sourceUrl}
            />
          }
        </Suspense>
        <div
          id={elementId}
          className={documentTextContentClassName}
          lang={languageCode}
          ref={setElementRef}
        />
        <SaveLinkInAppPopover
          contentRef={elementRef}
          focusTargetRef={focusTargetRef}
          isChunked={chunks.document.length > 1}
        />
        <WebContentFramePuppeteer
          chunks={chunks}
          shouldManageChunkedContent
          contentContainer={element}
          containerNodeSelector={`#${elementId},doesnt-exist[key="${docId}"]`}
          docId={docId}
          highlightIdToScrollTo={highlightIdToOpenAt}
          isActive={isContentFrameManagerActive}
          isAutoHighlightingEnabled={isAutoHighlightingEnabled}
          mustUseContentContainerArgument
          onHighlightElementsChanged={setHighlightElements}
          onFailedExtensionOrYouTubeHighlightIdsUpdated={
            setFailedExtensionOrYouTubeHighlightsHighlightIds
          }
          shouldProgressivelyRenderHighlights={shouldProgressivelyRenderHighlights}
          anchorScrollTarget={anchorScrollTarget}
          sourceUrl={sourceUrl || ''}
          setChunkContentState={setChunkContentState}
          sanitizationOptions={sanitizationOptions}
        />
        {!isResizingYouTubeVideo &&
          <ContentFocusIndicator
            contentRootRef={elementRef as React.MutableRefObject<HTMLDivElement>}
            docId={docIdRef.current}
            expectInitialExternalScroll={
              typeof initialScrollLocation?.scrollDepth === 'number' &&
              initialScrollLocation.scrollDepth > 0
            }
            initialSerializedScrollPosition={initialContentFocusIndicatorPosition}
            onNewFocusTarget={onFocusTargetChange}
            ref={focusTargetRef as React.MutableRefObject<HTMLElement>}
            scrollableAncestorRef={scrollableAncestorRef}
            refreshKey={chunks.document.length}
            isYouTubeVideo={isYouTubeVideo}
            isVideoHeaderShown={isVideoHeaderShown}
          />
        }
        {!isReading &&
          <ReturnToReadingButton
            onClick={returnToReadingPosition}
            currentScrollPos={latestScrollPosition}
            currentReadingPos={readingPosition}
          />
        }
        {isContentFrameManagerActive && canRenderPopovers &&
          <HighlighterPopovers
            eventEmitter={foregroundEventEmitter}
            highlightElements={highlightElements}
            onPositioningModeUpdated={setShouldHighlightIconsBeHidden}
          />
        }
      </div>
    );
  }),
);

// defaultExport.whyDidYouRender = {
//   trackHooks: true,
//   logOnDifferentValues: true,
// };

export default defaultExport;
